import { UserRole } from '../types';
import { createInviteAction } from '../authActions';
import { Input } from '../../../components/Form/Input';
import { Select } from '../../../components/Form/Select';
import { useModalStore } from '../../../shared/store/uiStore';
import { OrganizationUser } from '../../../shared/organization/types';
import { useState } from 'react';
import { updateOrgMembersActions } from '../../../shared/organization/organizationActions';
import { validateEmail } from '@auth/utils';

type InviteFormData = {
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
};

const InviteForm = ({ user }: { user?: OrganizationUser }) => {
  const closeModal = useModalStore((state) => state.closeModal);
  const setLoading = useModalStore((state) => state.setLoading);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: (user?.role as UserRole) || '',
  });
  const [message, setMessage] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const { firstName, lastName, email, role } = formData;
    try {
      if (user) {
        await updateOrgMembersActions([
          {
            type: 'set',
            uid: user.id,
            data: { role: role as UserRole },
          },
        ]);
        closeModal();
        return;
      }
      if (!validateEmail(email)) {
        setMessage('Invalid email address');
        return;
      }
      await createInviteAction(firstName, lastName, email, role);
    } catch (error) {
      console.error('Error creating invite:', error);
      if (error instanceof Error) {
        setMessage(error.message || 'Error creating invite');
      } else {
        setMessage('Error creating invite');
      }
    } finally {
      setLoading(false);
    }
    closeModal();
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev: InviteFormData) => ({ ...prev, [name]: value }));
  };

  return (
    <form
      id="invite-form"
      name="invite-form"
      onSubmit={handleSubmit}
      className="space-y-4 w-full flex flex-col"
    >
      {message && (
        <div className="text-red-500 text-sm text-center mb-2">{message}</div>
      )}
      {!user && (
        <div className="flex justify-between space-x-2 items-center">
          <Input
            type="text"
            value={formData.firstName}
            name="firstName"
            label="First Name"
            placeholder="Enter first name"
            required
            onChange={handleChange}
            className="w-full"
          />
          <Input
            type="text"
            value={formData.lastName}
            name="lastName"
            label="Last Name"
            placeholder="Enter last name"
            required
            className="w-full"
            onChange={handleChange}
          />
        </div>
      )}
      <div className="flex space-x-2 items-center">
        <Select
          name="role"
          value={formData.role}
          onChange={handleChange}
          label="Role"
          width={user ? 'w-full' : 'w-1/3'}
          options={[
            { value: '', label: 'Select role' },
            { value: 'editor', label: 'Editor' },
            { value: 'viewer', label: 'Viewer' },
          ]}
          required
        />
        {!user && (
          <Input
            type="email"
            value={formData.email}
            onChange={handleChange}
            name="email"
            label="Email"
            width="w-2/3"
            placeholder="Enter email"
            required
            pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$"
            className="w-full"
          />
        )}
      </div>
    </form>
  );
};
export default InviteForm;
